// Trims the silence a voice model leaves before and after a spoken line, so
// the 5-second limit is spent on speech and the file starts on the first word.
// Measured on eleven_v4 samples (2026-10-06): 0 to 0.06 s before the first
// word and 0.12 to 0.16 s after the last, below -45 dBFS.

import { trimAudio } from '@/lib/audio-utils'

export interface SilenceTrimOptions {
  /** Anything quieter than this, in dBFS, counts as silence. */
  thresholdDb?: number
  /** Room kept before the first sound, in seconds. */
  leadPadSeconds?: number
  /** Room kept after the last sound, in seconds. The 50 ms fade-out lands in it. */
  tailPadSeconds?: number
}

export const SILENCE_TRIM_DEFAULTS = {
  thresholdDb: -45,
  leadPadSeconds: 0.02,
  tailPadSeconds: 0.08,
} as const

export interface SpeechBounds {
  /** First sample kept. */
  startSample: number
  /** One past the last sample kept. */
  endSample: number
}

/** Where the sound starts and ends, or null when every sample is below the threshold. */
export function findSpeechBounds(
  channels: ArrayLike<number>[],
  sampleRate: number,
  options: SilenceTrimOptions = {}
): SpeechBounds | null {
  const { thresholdDb, leadPadSeconds, tailPadSeconds } = { ...SILENCE_TRIM_DEFAULTS, ...options }
  const threshold = 10 ** (thresholdDb / 20)
  const length = channels[0]?.length ?? 0

  let first = -1
  let last = -1
  for (let i = 0; i < length; i++) {
    if (channels.some(channel => Math.abs(channel[i]) > threshold)) {
      if (first === -1) first = i
      last = i
    }
  }
  if (first === -1) return null

  return {
    startSample: Math.max(0, first - Math.round(leadPadSeconds * sampleRate)),
    endSample: Math.min(length, last + 1 + Math.round(tailPadSeconds * sampleRate)),
  }
}

/** A clip with less than this much sound is treated as silent. */
export const MIN_AUDIBLE_SECONDS = 0.2

export class SilentClipError extends Error {
  constructor() {
    super('The clip came back silent, so there is nothing to download. Try a different voice or a different line.')
    this.name = 'SilentClipError'
  }
}

export interface SilenceTrimResult {
  buffer: AudioBuffer
  leadTrimmedSeconds: number
  tailTrimmedSeconds: number
}

/** Throws SilentClipError when nothing audible is left, so a silent WAV is never produced. */
export function trimSilence(buffer: AudioBuffer, options: SilenceTrimOptions = {}): SilenceTrimResult {
  const channels = Array.from({ length: buffer.numberOfChannels }, (_, channel) => buffer.getChannelData(channel))
  const bounds = findSpeechBounds(channels, buffer.sampleRate, options)
  if (!bounds) throw new SilentClipError()
  if ((bounds.endSample - bounds.startSample) / buffer.sampleRate < MIN_AUDIBLE_SECONDS) throw new SilentClipError()

  const leadTrimmedSeconds = bounds.startSample / buffer.sampleRate
  const tailTrimmedSeconds = (buffer.length - bounds.endSample) / buffer.sampleRate
  if (bounds.startSample === 0 && bounds.endSample === buffer.length) {
    return { buffer, leadTrimmedSeconds, tailTrimmedSeconds }
  }
  return {
    buffer: trimAudio(buffer, leadTrimmedSeconds, bounds.endSample / buffer.sampleRate),
    leadTrimmedSeconds,
    tailTrimmedSeconds,
  }
}
